import React, { useState, useRef } from 'react';
import { FaMousePointer, FaFont, FaImage, FaSmile, FaSquare, FaCircle, FaMinus, FaExpand } from 'react-icons/fa';
import useEditorStore from '../store/useEditorStore';
import EmojiPicker from './EmojiPicker';
import ShapePicker from './ShapePicker';

const toolButtonBase = "relative flex h-9 w-9 items-center justify-center rounded-lg cursor-pointer transition-all duration-150 after:absolute after:left-full after:top-1/2 after:-translate-y-1/2 after:z-[1000] after:ml-3 after:whitespace-nowrap after:rounded-md after:bg-gray-900 after:px-2 after:py-1 after:text-xs after:font-medium after:text-white after:opacity-0 after:scale-95 after:translate-x-[-4px] after:transition-all after:duration-300 after:ease-out after:pointer-events-none after:content-[attr(data-tooltip)] hover:after:opacity-100 hover:after:scale-100 hover:after:translate-x-0 before:absolute before:left-full before:top-1/2 before:-translate-y-1/2 before:z-[1000] before:ml-1 before:border-[5px] before:border-transparent before:border-r-gray-900 before:opacity-0 before:scale-95 before:translate-x-[-4px] before:transition-all before:duration-300 before:ease-out before:pointer-events-none before:content-[''] hover:before:opacity-100 hover:before:scale-100 hover:before:translate-x-0 dark:after:bg-gray-100 dark:after:text-gray-900 dark:before:border-r-gray-100";
const activeClass = "bg-primary/10 text-primary dark:bg-primary/20 dark:text-blue-400";
const inactiveClass = "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10";

const Divider = () => (
  <div className="mx-auto my-1 h-px w-7 bg-gray-200 dark:bg-gray-700" />
);

const Toolbar: React.FC = () => {
  const { selectedTool, setSelectedTool } = useEditorStore();
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isShapePickerOpen, setIsShapePickerOpen] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const shapeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative z-[200] flex h-full w-14 flex-col items-center border-r border-gray-200/60 bg-white/90 py-2 backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-900/90">
      <div className="flex flex-col items-center gap-1">
        {/* Group 1: Select */}
        <button
          onClick={() => setSelectedTool('select')}
          data-tooltip="Select"
          className={`${toolButtonBase} ${selectedTool === 'select' ? activeClass : inactiveClass}`}
        >
          <FaMousePointer size={14} />
        </button>

        <Divider />

        {/* Group 2: Text, Image */}
        <button
          onClick={() => setSelectedTool('text')}
          data-tooltip="Text"
          className={`${toolButtonBase} ${selectedTool === 'text' ? activeClass : inactiveClass}`}
        >
          <FaFont size={14} />
        </button>
        <button
          onClick={() => setSelectedTool('image')}
          data-tooltip="Image"
          className={`${toolButtonBase} ${selectedTool === 'image' ? activeClass : inactiveClass}`}
        >
          <FaImage size={14} />
        </button>

        <Divider />

        {/* Group 3: Rectangle, Circle, Line, Shapes */}
        <button
          onClick={() => setSelectedTool('rectangle')}
          data-tooltip="Rectangle"
          className={`${toolButtonBase} ${selectedTool === 'rectangle' ? activeClass : inactiveClass}`}
        >
          <FaSquare size={14} />
        </button>
        <button
          onClick={() => setSelectedTool('circle')}
          data-tooltip="Circle"
          className={`${toolButtonBase} ${selectedTool === 'circle' ? activeClass : inactiveClass}`}
        >
          <FaCircle size={14} />
        </button>
        <button
          onClick={() => setSelectedTool('line')}
          data-tooltip="Line"
          className={`${toolButtonBase} ${selectedTool === 'line' ? activeClass : inactiveClass}`}
        >
          <FaMinus size={14} />
        </button>
        <button
          ref={shapeButtonRef}
          onClick={() => setIsShapePickerOpen(!isShapePickerOpen)}
          data-tooltip="Shapes"
          className={`${toolButtonBase} ${isShapePickerOpen ? activeClass : inactiveClass}`}
        >
          <span className="material-symbols-rounded text-[18px]">category</span>
        </button>

        <Divider />

        {/* Group 4: Transform */}
        <button
          onClick={() => setSelectedTool('perspective')}
          data-tooltip="Perspective"
          className={`${toolButtonBase} ${selectedTool === 'perspective' ? activeClass : inactiveClass}`}
        >
          <FaExpand size={14} />
        </button>

        <Divider />

        {/* Group 5: Emoji */}
        <button
          ref={emojiButtonRef}
          onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
          data-tooltip="Emoji"
          className={`${toolButtonBase} ${isEmojiPickerOpen ? activeClass : inactiveClass}`}
        >
          <FaSmile size={14} />
        </button>
      </div>
      <ShapePicker
        isOpen={isShapePickerOpen}
        onClose={() => setIsShapePickerOpen(false)}
        triggerRef={shapeButtonRef}
      />
      <EmojiPicker
        isOpen={isEmojiPickerOpen}
        onClose={() => setIsEmojiPickerOpen(false)}
        triggerRef={emojiButtonRef}
      />
    </div>
  );
};

export default Toolbar;
